import {
  useGetPsychologistIcbtCommunication,
  useGetPsychologistIcbtTasks,
} from "api/useIcbtQueries";
import { useCategorizedTodoCommunication } from "components/icbt/hooks/useCategorizedTodoCommunication";
import { useSortedIcbtTodoTasks } from "components/icbt/hooks/useSortedIcbtTodoTasks";
import { useFlag } from "@mindlercare/unleash-client";
import { isPast, isToday } from "date-fns";
import { usePsychologistUser } from "hooks";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "../../../api/apiv2";
import { PopupWindow } from "./PopupWindow";
import { MAX_NOTIFICATIONS_IN_POPUP } from "./notifications.constants";
import { NotificationDisplayItem } from "./notifications.types";
import { PopoverTrigger } from "./PopoverTrigger";

const isOverdue = (feedbackDate: string) => {
  const date = new Date(feedbackDate);
  return isPast(date) && !isToday(date);
};

export const IcbtNotifications = () => {
  const { t } = useTranslation();
  const psychologistId = usePsychologistUser().psychologist?.psychologistId;
  const isAssessmentEnabled = useFlag("ICBT_ASSESSMENT_FIELD");

  const { data } = useGetPsychologistIcbtCommunication(undefined, {
    enabled: !!psychologistId && !isAssessmentEnabled,
  });

  const { data: icbtTasks } = useGetPsychologistIcbtTasks(undefined, {
    enabled: !!psychologistId && isAssessmentEnabled,
  });

  const api = useMindlerApi();

  const patientUserIdsIcbtComms = useMemo(
    () => [
      ...new Set(
        data?.icbtCommunications.map((icbt) => Number(icbt.patient_user_id)),
      ),
    ],
    [data],
  ).map((id) => ({
    userId: id,
  }));

  const patientUserIdsIcbtTasks = useMemo(
    () => [...new Set(icbtTasks?.map((task) => Number(task.patientUserId)))],
    [icbtTasks],
  ).map((id) => ({
    userId: id,
  }));

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const categorizedIcbtCommunication = useCategorizedTodoCommunication({
    icbtCommunication: data?.icbtCommunications,
    patientsToIncludeInCommunication: patientUserIdsIcbtComms,
    includeOnlyWithResponses: true,
    ignoreFeedbackDateFeatureEnabled,
  });

  const sortedIcbtTasks = useSortedIcbtTodoTasks({
    icbtTasks,
    patientsToIncludeInCommunication: patientUserIdsIcbtTasks,
  });

  const numberOfNotifications = isAssessmentEnabled
    ? (sortedIcbtTasks.get("now")?.length ?? 0)
    : (categorizedIcbtCommunication.get("today")?.length ?? 0);

  const notificationUserIds = isAssessmentEnabled
    ? [
        ...new Set(
          sortedIcbtTasks
            .get("now")
            ?.slice(0, MAX_NOTIFICATIONS_IN_POPUP)
            .map((h) => Number(h.patientUserId)),
        ),
      ]
    : [
        ...new Set(
          categorizedIcbtCommunication
            .get("today")
            ?.slice(0, MAX_NOTIFICATIONS_IN_POPUP)
            .map((h) => Number(h.patient_user_id)),
        ),
      ];

  const { data: patients } = useQuery(
    api.patients.getByUserIds.queryOptions(
      // Since we use the notificationUserIds we know that the userIds array is capped at length MAX_NOTIFICATIONS_IN_POPUP
      {
        userIds: notificationUserIds as number[], //safe to cast because of enabled condition
        psychologistId: psychologistId as number, // safe to cast because of the enabled condition
      },
      {
        enabled:
          notificationUserIds &&
          notificationUserIds?.length > 0 &&
          !!psychologistId,
      },
    ),
  );

  const categorizedIcbtCommunicationWithPatientInfo =
    useCategorizedTodoCommunication({
      icbtCommunication: data?.icbtCommunications,
      patientsToIncludeInCommunication: patients,
      includeOnlyWithResponses: true,
      ignoreFeedbackDateFeatureEnabled,
    });

  const sortedIcbtTasksWithPatientInfo = useSortedIcbtTodoTasks({
    icbtTasks,
    patientsToIncludeInCommunication: patients,
  });

  if (!psychologistId) {
    // pure admins have no use for this feature
    return null;
  }

  const notificationItems: NotificationDisplayItem[] = isAssessmentEnabled
    ? (sortedIcbtTasksWithPatientInfo.get("now") ?? []).map((task) => ({
        request_id: task.icbtRequestId,
        feedback_date: task.feedbackDate,
        overdue: isOverdue(task.feedbackDate),
        actions: task.tasks.map((icbtTask) => icbtTask.type),
        patient: task.patient,
      }))
    : (categorizedIcbtCommunicationWithPatientInfo.get("today") ?? []).map(
        (comm) => ({
          request_id: comm.request_id,
          feedback_date: comm.feedback_date,
          overdue: isOverdue(comm.feedback_date),
          actions: ["give_feedback" as const],
          patient: comm.patient,
        }),
      );

  return (
    <PopoverTrigger
      label={t("OPEN_NOTIFICATIONS")}
      placement={"bottom"}
      crossOffset={50}
      numberOfNotifications={numberOfNotifications}
    >
      <PopupWindow icbtCommunications={notificationItems} />
    </PopoverTrigger>
  );
};
